import { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  collection,
  addDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  doc,
  where,
  query,
} from "firebase/firestore";
import { auth, db } from "/src/firebaseConfig.js";
import { toDisplayDate, toStorageDate } from "../../utils/date";

// Convert mm:ss to total seconds
const timeToSeconds = (value) => {
  if (!value) return null;
  const parts = value.trim().split(":");
  if (parts.length !== 2) return null;
  const [mStr, sStr] = parts;
  const minutes = Number(mStr);
  const seconds = Number(sStr);
  if (
    Number.isNaN(minutes) ||
    Number.isNaN(seconds) ||
    minutes < 0 ||
    seconds < 0 ||
    seconds > 59
  )
    return null;
  return minutes * 60 + seconds;
};

// Convert seconds to mm:ss
const secondsToMMSS = (sec = 0) => {
  const total = Number(sec) || 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function StefanTimeChartGeneric({
  collectionName,
  title,
  valueLabel,
  color = "#f97316",
  filterUid = null,
  filterEmail = null,
  overrideUid = null,
  overrideEmail = null,
}) {
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ date: "", time: "" });
  const [editingId, setEditingId] = useState(null);

  // helper tipTraseu din numele colectiei
  const tipTraseu =
    collectionName && collectionName.includes("_")
      ? collectionName.split("_")[1]?.toUpperCase()
      : collectionName?.toUpperCase();

  // ================= FIRESTORE =================
  useEffect(() => {
    const baseRef = collection(db, collectionName);
    if (!filterUid && !filterEmail) {
      const unsub = onSnapshot(baseRef, (snapshot) => {
        const arr = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setData(arr);
      });
      return () => unsub();
    }

    const buffer = new Map();
    const collect = (snapshot) => {
      snapshot.docs.forEach((d) => buffer.set(d.id, { id: d.id, ...d.data() }));
      setData(Array.from(buffer.values()));
    };

    const unsubs = [];
    if (filterUid) {
      unsubs.push(onSnapshot(query(baseRef, where("uid", "==", filterUid)), collect));
      unsubs.push(onSnapshot(query(baseRef, where("userId", "==", filterUid)), collect));
    }
    if (filterEmail) {
      unsubs.push(onSnapshot(query(baseRef, where("userEmail", "==", filterEmail)), collect));
      unsubs.push(onSnapshot(query(baseRef, where("email", "==", filterEmail)), collect));
    }

    return () => unsubs.forEach((u) => u && u());
  }, [collectionName, filterUid, filterEmail]);

  // ================= HANDLE FORM =================
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({ date: "", time: "" });
    setEditingId(null);
  };

  const addEntry = async () => {
    const secondsValue = timeToSeconds(form.time);
    if (!form.date || secondsValue === null) return;
    const dateValue = toStorageDate(form.date);
    if (!dateValue) return;

    await addDoc(collection(db, collectionName), {
      date: dateValue,
      seconds: secondsValue,
      tipTraseu,
      uid: overrideUid || auth.currentUser?.uid || null,
      userEmail: overrideEmail || auth.currentUser?.email || null,
      createdAt: new Date().toISOString(),
    });

    resetForm();
  };

  const saveEdit = async () => {
    const secondsValue = timeToSeconds(form.time);
    if (!editingId || !form.date || secondsValue === null) return;
    const dateValue = toStorageDate(form.date);
    if (!dateValue) return;

    await updateDoc(doc(db, collectionName, editingId), {
      date: dateValue,
      seconds: secondsValue,
      tipTraseu,
      uid: overrideUid || auth.currentUser?.uid || null,
      userEmail: overrideEmail || auth.currentUser?.email || null,
    });

    resetForm();
  };

  const deleteEntry = async (id) => {
    await deleteDoc(doc(db, collectionName, id));
    if (id === editingId) resetForm();
  };

  const startEdit = (entry) => {
    setForm({
      date: toDisplayDate(entry.date, ""),
      time: secondsToMMSS(entry.seconds),
    });
    setEditingId(entry.id);
  };

  const surfaceStyle = {
    backgroundColor: "var(--color-bg)",
    border: "1px solid var(--color-border, #d1d5db)",
    color: "var(--color-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-input-bg, #f9fafb)",
    color: "#000",
    border: "1px solid var(--color-border, #d1d5db)",
  };

  const secondaryText = { color: "var(--color-text-secondary, #6b7280)" };
  const shouldScroll = data.length > 2;
  const chartData = data.map((entry) => ({
    ...entry,
    date: toDisplayDate(entry.date),
  }));

  return (
    <div className="w-full rounded-xl shadow-sm p-5" style={surfaceStyle}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold" style={{ color: "var(--color-text)" }}>
          {title}
        </h2>
        {editingId && (
          <span className="px-3 py-1 text-sm font-semibold rounded-full bg-orange-100 text-orange-700">
            Editare
          </span>
        )}
      </div>

      {/* ================= FORM ================= */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <input
          type="text"
          name="date"
          placeholder="Data (11/12/2025)"
          value={form.date}
          onChange={handleChange}
          className="p-3 rounded-lg w-full"
          style={inputStyle}
        />

        <input
          type="text"
          name="time"
          placeholder={`${valueLabel} (mm:ss)`}
          value={form.time}
          onChange={handleChange}
          className="p-3 rounded-lg w-full"
          style={inputStyle}
        />

        {editingId ? (
          <div className="flex gap-2 w-full md:w-auto">
            <button
              onClick={saveEdit}
              className="px-5 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition w-full md:w-auto"
            >
              Salvează
            </button>
            <button
              onClick={resetForm}
              className="px-5 py-3 rounded-lg font-semibold border transition w-full md:w-auto"
              style={{
                borderColor: "var(--color-border, #d1d5db)",
                color: "var(--color-text-secondary, #6b7280)",
              }}
            >
              Renunță
            </button>
          </div>
        ) : (
          <button
            onClick={addEntry}
            className="px-6 py-3 bg-orange-500 rounded-lg font-semibold hover:bg-orange-600 transition w-full md:w-auto"
            style={{ color: "#fff" }}
          >
            Adaugă
          </button>
        )}
      </div>

      {/* ================= LISTĂ ================= */}
      <div
        className="space-y-3 mb-8"
        style={
          shouldScroll
            ? {
                maxHeight: "220px",
                overflowY: "auto",
                paddingRight: "4px",
              }
            : undefined
        }
      >
        {data.map((entry) => (
          <div
            key={entry.id}
            className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 rounded-lg"
            style={{
              border: "1px solid var(--color-border, #d1d5db)",
              backgroundColor: "var(--color-surface, rgba(0,0,0,0.02))",
            }}
          >
            <p className="m-0 text-base" style={secondaryText}>
              <span className="font-semibold" style={{ color }}>
                Data:
              </span>{" "}
              {toDisplayDate(entry.date)}
              {"  |  "}
              <span className="font-semibold" style={{ color }}>
                {valueLabel}:
              </span>{" "}
              {secondsToMMSS(entry.seconds)}
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => startEdit(entry)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Editează
              </button>

              <button
                onClick={() => deleteEntry(entry.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition"
              >
                Șterge
              </button>
            </div>
          </div>
        ))}

        {data.length === 0 && (
          <p className="text-sm" style={secondaryText}>
            Nu există date salvate încă.
          </p>
        )}
      </div>

      {/* ================= CHART ================= */}
      <div
        className="h-[300px] rounded-lg border relative"
        style={{
          borderColor: "var(--color-border, #d1d5db)",
          backgroundColor: "var(--color-bg)",
        }}
      >
        {data.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm" style={secondaryText}>
            Introduceti date pentru generare chart
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, bottom: 40 }}>
              <defs>
                <linearGradient
                  id={`${collectionName}Gradient`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey="date"
                tick={{ fill: "var(--color-text-secondary, #6b7280)", fontSize: 12 }}
                angle={-90}
                textAnchor="end"
                interval={0}
                height={60}
              />

              <YAxis
                tickFormatter={(value) => secondsToMMSS(value)}
                tick={{ fill: "var(--color-text-secondary, #6b7280)", fontSize: 12 }}
              />

              <Tooltip
                formatter={(value) => secondsToMMSS(value)}
                labelStyle={{ color: color }}
                contentStyle={{
                  backgroundColor: "var(--color-bg)",
                  border: "1px solid var(--color-border, #d1d5db)",
                  borderRadius: "10px",
                  color: "var(--color-text)",
                }}
              />

              <Area
                type="monotone"
                dataKey="seconds"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${collectionName}Gradient)`}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
